import { Check, X } from 'lucide-react';
import CTAButton from '../components/CTAButton';

export default function ComparisonPage() {
  const comparisonData = [
    {
      feature: 'Commissions & Fees',
      quickClose: 'Zero commissions',
      traditional: '~6% agent commission + other fees'
    },
    {
      feature: 'Closing Costs',
      quickClose: 'We pay all closing costs',
      traditional: 'Seller typically pays 1-3%'
    },
    {
      feature: 'Repairs & Inspections',
      quickClose: 'None required—we buy as-is',
      traditional: 'Often required; reduces offer'
    },
    {
      feature: 'Financing Contingency',
      quickClose: 'None—we have cash',
      traditional: 'Common risk—deals often fall through'
    },
    {
      feature: 'Appraisal Required',
      quickClose: 'No appraisal needed',
      traditional: 'Yes, required by lenders'
    },
    {
      feature: 'Showings',
      quickClose: 'Minimal or none',
      traditional: 'Many showings, open houses'
    },
    {
      feature: 'Closing Timeline',
      quickClose: 'As fast as 7 days or your timeline',
      traditional: '30-60+ days typical'
    },
    {
      feature: 'Closing Date Control',
      quickClose: 'You choose the date',
      traditional: 'Depends on buyer, lender, inspector'
    },
    {
      feature: 'Privacy',
      quickClose: 'Completely private process',
      traditional: 'Property shown publicly'
    },
    {
      feature: 'Certainty',
      quickClose: 'Cash offer—no contingencies',
      traditional: 'Buyer can back out for many reasons'
    }
  ];

  return (
    <div className="min-h-screen">
      <section className="pt-12 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Quick Close vs Traditional Sale</h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Not all ways of selling a home are the same. See how Quick Close compares to listing with a traditional real estate agent.
          </p>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-6 font-bold text-gray-900">Feature</th>
                  <th className="text-center py-4 px-6">
                    <div className="text-lg font-bold text-blue-900">Quick Close</div>
                    <div className="text-sm text-gray-500">Direct Cash Buyer</div>
                  </th>
                  <th className="text-center py-4 px-6">
                    <div className="text-lg font-bold text-red-600">Traditional Sale</div>
                    <div className="text-sm text-red-500">Listed with Agent</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="py-6 px-6 font-semibold text-gray-900">{row.feature}</td>
                    <td className="py-6 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Check className="w-5 h-5 text-blue-900" />
                        <span className="text-blue-900 font-medium">{row.quickClose}</span>
                      </div>
                    </td>
                    <td className="py-6 px-6 text-center">
                      <span className="text-red-600 font-medium">{row.traditional}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Why This Matters to You</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Save Money</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Traditional sales cost 6-9% of your home's value in commissions and fees. With Quick Close, there are no commissions, no inspection costs, and no appraisal fees. We pay closing costs.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg text-sm font-semibold text-blue-900">
                Example: On a $300,000 home, you could save $18,000-$27,000
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Save Time</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Traditional sales take 30-60+ days. With Quick Close, you can close in as little as 7 days if you need to. No waiting months for showings, offers, or financing approvals.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg text-sm font-semibold text-blue-900">
                Fast cash offer + closing on your timeline
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Reduce Stress</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                No showings, no open houses, no public listing, no inspection contingencies, no appraisals, and no worrying about deals falling through. Quick Close means simplicity and certainty.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg text-sm font-semibold text-blue-900">
                Private process + cash certainty = peace of mind
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Control Your Timeline</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                With a traditional sale, closing date depends on the buyer's mortgage approval, inspector schedule, and more. With Quick Close, you pick when to close.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg text-sm font-semibold text-blue-900">
                Close on your schedule, not someone else's
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">When Each Option Makes Sense</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-blue-900 bg-blue-50">
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Choose Quick Close If:</h3>
              <ul className="space-y-3">
                {[
                  'You need to sell quickly',
                  'Your home needs significant repairs',
                  'You want to avoid traditional agent hassle',
                  'Privacy is important to you',
                  'You want certainty of closing',
                  'You want to minimize costs',
                  'You\'re facing foreclosure or other urgent situations',
                  'You don\'t want to deal with showings or open houses'
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-3">
                    <Check className="w-5 h-5 text-blue-900 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Traditional Sale May Be Better If:</h3>
              <ul className="space-y-3">
                {[
                  'Your home is in excellent condition',
                  "You're in a competitive seller's market",
                  'You have time to wait (30-60+ days)',
                  "You're willing to do showings and open houses",
                  "You don't need to sell quickly",
                  'Maximum sale price is your only priority'
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-3">
                    <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Common Questions About the Comparison</h2>
          <div className="space-y-6">
            {[
              {
                q: 'Will I get less money with Quick Close?',
                a: 'Not necessarily. While our offer may be slightly lower than a potential market sale, when you factor in 6% commissions, closing costs, repairs, and time value, Quick Close often nets you more money in your pocket.'
              },
              {
                q: 'Can I get a second opinion on the value?',
                a: 'Our offers are based on real market analysis and comparable sales. We\'re transparent about how we arrive at our number and happy to walk you through our evaluation process in detail.'
              },
              {
                q: 'What if the market is really hot right now?',
                a: 'In a hot seller\'s market, traditional listing might get a higher price. But you\'ll still pay 6% in commissions, wait 30-60 days, and deal with the stress of showings and contingencies. Quick Close offers speed and certainty.'
              },
              {
                q: 'Why would I choose Quick Close over listing?',
                a: 'The main reasons: speed, simplicity, privacy, certainty, and no repairs needed. If those matter to you, Quick Close is worth considering even if it\'s not the absolute highest price.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
                <h4 className="text-lg font-bold text-gray-900 mb-2">{faq.q}</h4>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 to-blue-950">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Not Sure Which Option Is Right?</h2>
          <p className="text-xl mb-8 text-blue-50 max-w-2xl mx-auto">
            Get a cash offer from us and compare. No obligation to proceed, and you'll have concrete numbers to make a decision.
          </p>
          <CTAButton text="Get Your Offer" size="lg" variant="primary" />
        </div>
      </section>
    </div>
  );
}
